import { useCallback, useMemo, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { getRankForIndex } from '@aline/common';
import {
  closestCenter,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  SelectiveMouseSensor,
  SelectivePointerSensor,
} from 'src/components/workflow/board/utils/keyboardCoordinateGetter';
import { useMovePlaybookRule, useMovePlaybookRuleCategory } from 'src/hooks';

import {
  findRuleCategoryId,
  findRuleDrop,
  getRuleOrder,
  moveRuleToCategory,
  moveRuleWithinCategory,
} from './rule-drag-order';
import type { RuleOrder } from './rule-drag-order';
import type { PlaybookDetail, PlaybookDetailRule } from './types';

/** How far a pointer travels before a press becomes a drag. */
const DRAG_ACTIVATION_DISTANCE_PX = 8;

/**
 * Dragging on the playbook detail page: rules between and within categories,
 * and categories within the playbook. One `DndContext` serves both, and which
 * kind of drag is happening is read off the active id — category ids are the
 * keys of the rule order, rule ids are in its lists.
 *
 * A category drag is a plain sortable list: the card itself follows the
 * pointer, its siblings shift out of the way, and the drop sends the
 * category's new index. Nothing is remounted, so there is no draft to keep.
 *
 * A rule drag is not: while it is in progress the list renders from a local
 * copy of the order (`draftOrder`), which `onDragOver` rewrites as the rule
 * crosses into another category — that is what makes the other category's
 * rules part to make room. The drop then sends one mutation naming the rule,
 * the category it landed in and its index there, and the draft is discarded:
 * the mutation writes the same order into the cache optimistically, so the
 * server order the list goes back to rendering from already matches what the
 * user saw.
 *
 * Either way the rank is generated twice on purpose. The server's, generated
 * under a lock from the neighbours it reads, is the one that is kept. The
 * client's, generated here from the neighbours it rendered, only fills the
 * optimistic row until the reply arrives — without it the cache would briefly
 * hold a row whose rank contradicts its place in the list.
 */
export function usePlaybookDragAndDrop(playbook: PlaybookDetail) {
  const { movePlaybookRule } = useMovePlaybookRule();
  const { movePlaybookRuleCategory } = useMovePlaybookRuleCategory();
  const [activeId, setActiveId] = useState<UUID | null>(null);
  const [draftOrder, setDraftOrder] = useState<RuleOrder | null>(null);

  const { categories } = playbook;
  const serverOrder = useMemo(() => getRuleOrder(categories), [categories]);
  const order = draftOrder ?? serverOrder;

  const rulesById = useMemo(
    () =>
      new Map<UUID, PlaybookDetailRule>(
        categories.flatMap(category => category.rules.map(rule => [rule.id, rule]))
      ),
    [categories]
  );
  const activeRule = activeId ? (rulesById.get(activeId) ?? null) : null;

  /**
   * What each category shows: the rules in `order`, resolved to their data.
   * A rule that is in the order but no longer in the playbook is left out.
   */
  const rulesByCategoryId = useMemo(
    (): Record<UUID, PlaybookDetailRule[]> =>
      Object.fromEntries(
        Object.entries(order).map(([categoryId, ruleIds]) => [
          categoryId,
          ruleIds.flatMap(ruleId => rulesById.get(ruleId) ?? []),
        ])
      ),
    [order, rulesById]
  );

  /**
   * Collision handling sees the order as it was when the last event fired, so
   * a ref lets it read the latest draft. The last hit is remembered for the
   * frames right after a rule changes category, when the layout shifts under
   * the pointer and nothing registers a hit; falling back to it stops the rule
   * flickering back to where it came from.
   */
  const orderRef = useRef(order);
  orderRef.current = order;
  const lastOverIdRef = useRef<string | null>(null);

  const sensors = useSensors(
    useSensor(SelectivePointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(SelectiveMouseSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    })
  );

  /**
   * A category drag only ever lands among the other category cards, so those
   * are the only targets it sees.
   *
   * A rule drag resolves in two steps: the category comes from whatever the
   * pointer is inside (a rule or a category card), then the target within it
   * is the rule whose centre the dragged row is nearest. The first step is
   * what moves a rule between categories; the second gives the usual sortable
   * feel within one, where a rule gives up its slot once the dragged row
   * passes its middle rather than the moment the pointer touches its edge.
   * Only an empty category is a target in its own right, which is what lets a
   * rule be dropped into it.
   */
  const collisionDetection: CollisionDetection = useCallback(args => {
    const currentOrder = orderRef.current;

    if (String(args.active.id) in currentOrder) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          container => String(container.id) in currentOrder
        ),
      });
    }

    const pointerCollisions = pointerWithin(args);
    const collisions = pointerCollisions.length ? pointerCollisions : rectIntersection(args);
    const overId = getFirstCollision(collisions, 'id');
    if (overId === null) {
      return lastOverIdRef.current ? [{ id: lastOverIdRef.current }] : [];
    }

    const categoryId = findRuleCategoryId(currentOrder, String(overId));
    if (!categoryId) {
      lastOverIdRef.current = String(overId);

      return collisions;
    }

    const ruleIds = currentOrder[categoryId];
    const ruleCollisions = ruleIds.length
      ? closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(container =>
            ruleIds.includes(String(container.id))
          ),
        })
      : [];
    const resolved = ruleCollisions.length ? ruleCollisions : [{ id: categoryId }];
    lastOverIdRef.current = String(resolved[0].id);

    return resolved;
  }, []);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent): void => {
      const id = String(active.id);
      setActiveId(id);
      // Only a rule drag needs the draft; a category card is never remounted.
      setDraftOrder(id in serverOrder ? null : serverOrder);
      lastOverIdRef.current = null;
    },
    [serverOrder]
  );

  const handleDragOver = useCallback(({ active, over }: DragOverEvent): void => {
    if (!over) return;

    const ruleId = String(active.id);
    const overId = String(over.id);
    setDraftOrder(current => {
      if (!current || ruleId in current) return current;

      const toCategoryId = findRuleCategoryId(current, overId);
      const fromCategoryId = findRuleCategoryId(current, ruleId);
      if (!toCategoryId || !fromCategoryId || toCategoryId === fromCategoryId) return current;

      /**
       * Whether the rule is being carried in from below the rule it is over,
       * in which case it should slot in after that rule rather than before it.
       */
      const draggedRect = active.rect.current.translated;
      const isBelowOver = Boolean(
        draggedRect && draggedRect.top > over.rect.top + over.rect.height
      );

      return moveRuleToCategory(current, { isBelowOver, overId, ruleId, toCategoryId });
    });
  }, []);

  const handleDragCancel = useCallback((): void => {
    setActiveId(null);
    setDraftOrder(null);
  }, []);

  const dropCategory = useCallback(
    (categoryId: UUID, overId: UUID): void => {
      const categoryIds = categories.map(category => category.id);
      const fromIndex = categoryIds.indexOf(categoryId);
      const toIndex = categoryIds.indexOf(overId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

      const category = categories[fromIndex];
      const siblingRanks = arrayMove(categories, fromIndex, toIndex)
        .filter(sibling => sibling.id !== categoryId)
        .map(sibling => sibling.rank);

      void movePlaybookRuleCategory({
        input: { categoryId, toIndex },
        optimisticCategory: { ...category, rank: getRankForIndex(siblingRanks, toIndex) },
        playbookId: playbook.id,
      });
    },
    [categories, movePlaybookRuleCategory, playbook.id]
  );

  const dropRule = useCallback(
    (ruleId: UUID, overId: UUID): void => {
      const rule = rulesById.get(ruleId);
      const startedAt = findRuleDrop(serverOrder, ruleId);
      if (!rule || !startedAt || !draftOrder) return;

      /**
       * Any cross-category move has already been applied to the draft by
       * `onDragOver`; what is left is the final slot within that category. Over
       * the category itself rather than a rule in it, the draft already has
       * the rule where it goes.
       */
      const overCategoryId = findRuleCategoryId(draftOrder, overId);
      const finalOrder = overCategoryId
        ? moveRuleWithinCategory(draftOrder, overCategoryId, ruleId, overId)
        : draftOrder;
      const drop = findRuleDrop(finalOrder, ruleId);
      if (!drop) return;
      if (drop.categoryId === startedAt.categoryId && drop.toIndex === startedAt.toIndex) return;

      const siblingIds = finalOrder[drop.categoryId].filter(id => id !== ruleId);
      const siblingRanks = siblingIds.flatMap(id => rulesById.get(id)?.rank ?? []);
      /**
       * A missing sibling means the list changed under the drag; the server
       * still lands the rule correctly, it just cannot be shown early.
       */
      const optimisticRule =
        siblingRanks.length === siblingIds.length
          ? { ...rule, rank: getRankForIndex(siblingRanks, drop.toIndex) }
          : undefined;

      void movePlaybookRule({
        fromCategoryId: startedAt.categoryId,
        input: { categoryId: drop.categoryId, ruleId, toIndex: drop.toIndex },
        optimisticRule,
      });
    },
    [draftOrder, movePlaybookRule, rulesById, serverOrder]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent): void => {
      /**
       * The optimistic cache write in the drop happens in the same render
       * batch as the draft being cleared here, so the list never shows the
       * old order in between.
       */
      setActiveId(null);
      setDraftOrder(null);
      if (!over) return;

      const id = String(active.id);
      const overId = String(over.id);
      if (id in serverOrder) dropCategory(id, overId);
      else dropRule(id, overId);
    },
    [dropCategory, dropRule, serverOrder]
  );

  return {
    activeRule,
    collisionDetection,
    handleDragCancel,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
    rulesByCategoryId,
    sensors,
  };
}
